import type { GetV2ProviderJotauditDashboardNotesApiArg } from '@store/generated/jotaudit';
import { enhancedApi } from '@store/generated/jotaudit';

/**
 * Feature-specific tag types for jotaudit
 */
const JOTAUDIT_TAG_TYPES = [
  'NoteAudit',
  'NoteAuditList',
  'EncounterAudit',
  'EncounterAuditList',
  'Provider',
  'ProviderList',
  'AuditRevision',
  'AuditRevisionList',
  'Organization',
  'OrganizationList',
  'GlobalStats',
] as const;

/**
 * JotAudit API - extends the generated API with custom tagging
 */
export const jotauditApi = enhancedApi.enhanceEndpoints({
  addTagTypes: JOTAUDIT_TAG_TYPES,
  endpoints: {
    // Provider endpoints
    // Override query function to filter out null values (backend rejects null)
    getV2ProviderJotauditDashboardNotes: {
      query: (queryArg?: GetV2ProviderJotauditDashboardNotesApiArg) => {
        const params: Record<string, string> = {};
        if (queryArg?.days !== undefined && queryArg.days !== null) {
          params.days = String(queryArg.days);
        }
        if (queryArg?.dateFrom) {
          params.date_from = queryArg.dateFrom;
        }
        if (queryArg?.dateTo) {
          params.date_to = queryArg.dateTo;
        }
        if (queryArg?.userIds) {
          params.user_ids = queryArg.userIds;
        }
        if (queryArg?.page !== undefined) {
          params.page = String(queryArg.page);
        }
        if (queryArg?.perPage !== undefined) {
          params.per_page = String(queryArg.perPage);
        }
        if (queryArg?.minQualityScore !== undefined && queryArg.minQualityScore !== null) {
          params.min_quality_score = String(queryArg.minQualityScore);
        }
        if (queryArg?.maxQualityScore !== undefined && queryArg.maxQualityScore !== null) {
          params.max_quality_score = String(queryArg.maxQualityScore);
        }
        if (queryArg?.cptMismatchOnly !== undefined && queryArg.cptMismatchOnly !== null) {
          params.cpt_mismatch_only = String(queryArg.cptMismatchOnly);
        }
        if (queryArg?.isCompleted !== undefined && queryArg.isCompleted !== null) {
          params.is_completed = String(queryArg.isCompleted);
        }
        return {
          url: `/v2/provider/jotaudit/dashboard/notes`,
          params,
        };
      },
      providesTags: (result: any) =>
        result && result.audits
          ? [
              ...result.audits.map((item: any) => ({
                type: 'NoteAudit' as const,
                id: item.audit?.note_audit_id || 'unknown',
              })),
              { type: 'NoteAuditList' as const, id: 'LIST' },
            ]
          : [{ type: 'NoteAuditList', id: 'LIST' }],
    },
    // New endpoint: /v2/provider/jotaudit/notes/{noteId}
    // Uses require_provider_auth (no practice_administrator role required)
    // Replaces old /v2/provider/jotaudit/dashboard/notes/{noteId} endpoint
    getV2ProviderJotauditNotesByNoteId: {
      providesTags: (result, _error, { noteId }) =>
        result && result.audit
          ? [
              { type: 'NoteAudit' as const, id: result.audit.note_audit_id },
              { type: 'NoteAuditList' as const, id: 'LIST' },
            ]
          : [{ type: 'NoteAudit', id: noteId }],
    },
    // Override to filter out null/undefined values from query params
    getV2ProviderJotauditDashboardProviders: {
      query: (queryArg: {
        days?: number | null;
        dateFrom?: string | null;
        dateTo?: string | null;
        userIds?: string | null;
      }) => {
        const params: Record<string, string> = {};
        if (queryArg.days !== undefined && queryArg.days !== null) {
          params.days = String(queryArg.days);
        }
        if (queryArg.dateFrom) {
          params.date_from = queryArg.dateFrom;
        }
        if (queryArg.dateTo) {
          params.date_to = queryArg.dateTo;
        }
        if (queryArg.userIds) {
          params.user_ids = queryArg.userIds;
        }
        return {
          url: `/v2/provider/jotaudit/dashboard/providers`,
          params,
        };
      },
      providesTags: result =>
        result && result.providers
          ? [
              ...result.providers.map(item => ({
                type: 'Provider' as const,
                id: item.user_id || 'unknown',
              })),
              { type: 'ProviderList' as const, id: 'LIST' },
            ]
          : [{ type: 'ProviderList', id: 'LIST' }],
    },
    getV2ProviderJotauditDashboardProvidersByUserId: {
      providesTags: (result, _error, { userId }) =>
        result
          ? [
              { type: 'Provider' as const, id: userId },
              { type: 'ProviderList' as const, id: 'LIST' },
            ]
          : [{ type: 'Provider', id: userId }],
    },
    // Encounter endpoints - override to filter out null/undefined values
    getV2ProviderJotauditDashboardEncounters: {
      query: (queryArg: {
        days?: number | null;
        dateFrom?: string | null;
        dateTo?: string | null;
        userIds?: string | null;
        hasAudit?: boolean | null;
        page?: number;
        perPage?: number;
      }) => {
        const params: Record<string, string> = {};
        if (queryArg.days !== undefined && queryArg.days !== null) {
          params.days = String(queryArg.days);
        }
        if (queryArg.dateFrom) {
          params.date_from = queryArg.dateFrom;
        }
        if (queryArg.dateTo) {
          params.date_to = queryArg.dateTo;
        }
        if (queryArg.userIds) {
          params.user_ids = queryArg.userIds;
        }
        if (queryArg.hasAudit !== undefined && queryArg.hasAudit !== null) {
          params.has_audit = String(queryArg.hasAudit);
        }
        if (queryArg.page !== undefined) {
          params.page = String(queryArg.page);
        }
        if (queryArg.perPage !== undefined) {
          params.per_page = String(queryArg.perPage);
        }
        return {
          url: `/v2/provider/jotaudit/dashboard/encounters`,
          params,
        };
      },
      providesTags: (result: any) =>
        result && result.encounters
          ? [
              ...result.encounters.map((item: any) => ({
                type: 'EncounterAudit' as const,
                id: item.encounter_id || 'unknown',
              })),
              { type: 'EncounterAuditList' as const, id: 'LIST' },
            ]
          : [{ type: 'EncounterAuditList', id: 'LIST' }],
    },
    getV2ProviderJotauditDashboardEncountersByEncounterId: {
      providesTags: (result, _error, { encounterId }) =>
        result
          ? [
              { type: 'EncounterAudit' as const, id: encounterId },
              { type: 'EncounterAuditList' as const, id: 'LIST' },
            ]
          : [{ type: 'EncounterAudit', id: encounterId }],
    },
    getV2ProviderJotauditDashboardRulesByRuleIdAudits: {
      providesTags: (result, _error, { ruleId }) =>
        result && result.revisions
          ? [
              ...result.revisions.map(item => ({
                type: 'AuditRevision' as const,
                id: `${ruleId}-${item.revision}`,
              })),
              { type: 'AuditRevisionList' as const, id: ruleId },
            ]
          : [{ type: 'AuditRevisionList', id: ruleId }],
    },
    // OpenAPI spec now includes filter params - override to filter out null/undefined values
    getV2ProviderJotauditDashboardRulesComplianceStats: {
      query: (queryArg: {
        days?: number | null;
        dateFrom?: string | null;
        dateTo?: string | null;
        userIds?: string | null;
        ruleIds?: string | null;
        comparisonMode?: boolean | null;
      }) => {
        const params: Record<string, string> = {};
        if (queryArg.days !== undefined && queryArg.days !== null) {
          params.days = String(queryArg.days);
        }
        if (queryArg.dateFrom) {
          params.date_from = queryArg.dateFrom;
        }
        if (queryArg.dateTo) {
          params.date_to = queryArg.dateTo;
        }
        if (queryArg.userIds) {
          params.user_ids = queryArg.userIds;
        }
        if (queryArg.ruleIds) {
          params.rule_ids = queryArg.ruleIds;
        }
        if (queryArg.comparisonMode !== undefined && queryArg.comparisonMode !== null) {
          params.comparison_mode = String(queryArg.comparisonMode);
        }
        return {
          url: `/v2/provider/jotaudit/dashboard/rules/compliance-stats`,
          params,
        };
      },
      providesTags: [{ type: 'NoteAuditList' as const, id: 'LIST' }],
    },
    // OpenAPI spec now includes filter params - override to filter out null/undefined values
    getV2ProviderJotauditDashboardRulesByRuleIdStats: {
      query: (queryArg: {
        ruleId: string;
        days?: number | null;
        dateFrom?: string | null;
        dateTo?: string | null;
        userIds?: string | null;
      }) => {
        const params: Record<string, string> = {};
        if (queryArg.days !== undefined && queryArg.days !== null) {
          params.days = String(queryArg.days);
        }
        if (queryArg.dateFrom) {
          params.date_from = queryArg.dateFrom;
        }
        if (queryArg.dateTo) {
          params.date_to = queryArg.dateTo;
        }
        if (queryArg.userIds) {
          params.user_ids = queryArg.userIds;
        }
        return {
          url: `/v2/provider/jotaudit/dashboard/rules/${queryArg.ruleId}/stats`,
          params,
        };
      },
      providesTags: (result, _error, { ruleId }) =>
        result ? [{ type: 'NoteAuditList' as const, id: ruleId }] : [],
    },
    // OpenAPI spec now includes filter params - override to filter out null/undefined values
    getV2ProviderJotauditDashboardRulesByRuleIdAuditsDrilldown: {
      query: (queryArg: {
        ruleId: string;
        complianceStatus?: string | null;
        days?: number | null;
        dateFrom?: string | null;
        dateTo?: string | null;
        userIds?: string | null;
        page?: number;
        perPage?: number;
      }) => {
        const params: Record<string, string> = {};
        if (queryArg.complianceStatus) {
          params.compliance_status = queryArg.complianceStatus;
        }
        if (queryArg.days !== undefined && queryArg.days !== null) {
          params.days = String(queryArg.days);
        }
        if (queryArg.dateFrom) {
          params.date_from = queryArg.dateFrom;
        }
        if (queryArg.dateTo) {
          params.date_to = queryArg.dateTo;
        }
        if (queryArg.userIds) {
          params.user_ids = queryArg.userIds;
        }
        if (queryArg.page !== undefined) {
          params.page = String(queryArg.page);
        }
        if (queryArg.perPage !== undefined) {
          params.per_page = String(queryArg.perPage);
        }
        return {
          url: `/v2/provider/jotaudit/dashboard/rules/${queryArg.ruleId}/audits/drilldown`,
          params,
        };
      },
      providesTags: (result, _error, { ruleId }) =>
        result ? [{ type: 'NoteAuditList' as const, id: `${ruleId}-drilldown` }] : [],
    },
    // Override to filter out null/undefined values from query params
    getV2ProviderJotauditDashboardStats: {
      query: (queryArg: {
        days?: number | null;
        dateFrom?: string | null;
        dateTo?: string | null;
        userIds?: string | null;
      }) => {
        const params: Record<string, string> = {};
        if (queryArg.days !== undefined && queryArg.days !== null) {
          params.days = String(queryArg.days);
        }
        if (queryArg.dateFrom) {
          params.date_from = queryArg.dateFrom;
        }
        if (queryArg.dateTo) {
          params.date_to = queryArg.dateTo;
        }
        if (queryArg.userIds) {
          params.user_ids = queryArg.userIds;
        }
        return {
          url: `/v2/provider/jotaudit/dashboard/stats`,
          params,
        };
      },
    },
    // Admin endpoints
    getV2AdminJotauditDashboardNotesByOrganizationId: {
      providesTags: (result, _error, { organizationId }) =>
        result && result.audits
          ? [
              ...result.audits.map(item => ({
                type: 'NoteAudit' as const,
                id: item.audit?.note_audit_id || 'unknown',
              })),
              { type: 'NoteAuditList' as const, id: organizationId },
            ]
          : [{ type: 'NoteAuditList', id: organizationId }],
    },
    getV2AdminJotauditDashboardProvidersByOrganizationId: {
      providesTags: (result, _error, { organizationId }) =>
        result && result.providers
          ? [
              ...result.providers.map(item => ({
                type: 'Provider' as const,
                id: item.user_id || 'unknown',
              })),
              { type: 'ProviderList' as const, id: organizationId },
            ]
          : [{ type: 'ProviderList', id: organizationId }],
    },
    // Admin encounter endpoints
    getV2AdminJotauditDashboardEncounters: {
      providesTags: (result: any) =>
        result && result.encounters
          ? [
              ...result.encounters.map((item: any) => ({
                type: 'EncounterAudit' as const,
                id: item.encounter_id || 'unknown',
              })),
              { type: 'EncounterAuditList' as const, id: 'GLOBAL' },
            ]
          : [{ type: 'EncounterAuditList', id: 'GLOBAL' }],
    },
    getV2AdminJotauditDashboardEncountersByEncounterId: {
      providesTags: (result, _error, { encounterId }) =>
        result
          ? [
              { type: 'EncounterAudit' as const, id: encounterId },
              { type: 'EncounterAuditList' as const, id: 'GLOBAL' },
            ]
          : [{ type: 'EncounterAudit', id: encounterId }],
    },
    // Global admin endpoints
    getV2AdminJotauditDashboardNotes: {
      providesTags: (result: any) =>
        result && result.audits
          ? [
              ...result.audits.map((item: any) => ({
                type: 'NoteAudit' as const,
                id: item.audit?.note_audit_id || 'unknown',
              })),
              { type: 'NoteAuditList' as const, id: 'GLOBAL' },
            ]
          : [{ type: 'NoteAuditList', id: 'GLOBAL' }],
    },
    getV2AdminJotauditDashboardOrganizations: {
      providesTags: (result: any) =>
        result && result.organizations
          ? [
              ...result.organizations.map((item: any) => ({
                type: 'Organization' as const,
                id: item.organization_id || 'unknown',
              })),
              { type: 'OrganizationList' as const, id: 'LIST' },
            ]
          : [{ type: 'OrganizationList', id: 'LIST' }],
    },
    getV2AdminJotauditDashboardProviders: {
      providesTags: (result: any) =>
        result && result.providers
          ? [
              ...result.providers.map((item: any) => ({
                type: 'Provider' as const,
                id: item.user_id || 'unknown',
              })),
              { type: 'ProviderList' as const, id: 'GLOBAL' },
            ]
          : [{ type: 'ProviderList', id: 'GLOBAL' }],
    },
    getV2AdminJotauditDashboardStats: {
      providesTags: [{ type: 'GlobalStats' as const, id: 'GLOBAL' }],
    },
    postV2AdminJotauditRun: {
      invalidatesTags: [
        { type: 'NoteAuditList', id: 'LIST' },
        { type: 'NoteAuditList', id: 'GLOBAL' },
        { type: 'GlobalStats', id: 'GLOBAL' },
      ],
    },
    // NOTE: No invalidatesTags here - we manually refetch at the end of SSE streaming
    // to avoid the mutation triggering a refetch that overwrites our cleared cache
    postV2ProviderJotauditNotesByNoteIdReaudit: {},
  },
});

// Export hooks with user-friendly names
export const {
  // Provider endpoints
  useGetV2ProviderJotauditDashboardNotesQuery: useGetDashboardNotesQuery,
  useGetV2ProviderJotauditNotesByNoteIdQuery: useGetNoteAuditDetailQuery,
  useGetV2ProviderJotauditDashboardNotesByNoteIdHistoryQuery: useGetNoteAuditHistoryQuery,
  useGetV2ProviderJotauditDashboardProvidersQuery: useGetDashboardProvidersQuery,
  useGetV2ProviderJotauditDashboardProvidersByUserIdQuery: useGetProviderSummaryQuery,
  useGetV2ProviderJotauditDashboardEncountersQuery: useGetDashboardEncountersQuery,
  useGetV2ProviderJotauditDashboardEncountersByEncounterIdQuery: useGetEncounterAuditDetailQuery,
  useGetV2ProviderJotauditDashboardRulesByRuleIdAuditsQuery: useGetRuleAuditsQuery,
  useGetV2ProviderJotauditDashboardRulesComplianceStatsQuery: useGetRuleComplianceStatsQuery,
  useGetV2ProviderJotauditDashboardRulesByRuleIdStatsQuery: useGetRuleDetailStatsQuery,
  useGetV2ProviderJotauditDashboardRulesByRuleIdAuditsDrilldownQuery: useGetRuleAuditsDrilldownQuery,
  useGetV2ProviderJotauditDashboardStatsQuery: useGetDashboardStatsQuery,
  // Admin endpoints - Organization-specific
  // Note: Admin note detail endpoints are now in adminApi.ts with organizationId in path
  useGetV2AdminJotauditDashboardNotesByOrganizationIdQuery: useGetAdminOrganizationNotesQuery,
  useGetV2AdminJotauditDashboardProvidersByOrganizationIdQuery:
    useGetAdminOrganizationProvidersQuery,
  useGetV2AdminJotauditDashboardProvidersByOrganizationIdAndUserIdQuery:
    useGetAdminProviderDetailQuery,
  // Note: Admin rule audits endpoint is now in adminApi.ts with organizationId in path
  useGetV2AdminJotauditDashboardStatsByOrganizationIdQuery: useGetAdminOrganizationStatsQuery,
  // Admin endpoints - Global (cross-organization)
  useGetV2AdminJotauditDashboardNotesQuery: useGetAdminGlobalNotesQuery,
  useGetV2AdminJotauditDashboardEncountersQuery: useGetAdminEncountersQuery,
  useGetV2AdminJotauditDashboardEncountersByEncounterIdQuery: useGetAdminEncounterDetailQuery,
  useGetV2AdminJotauditDashboardOrganizationsQuery: useGetAdminOrganizationsQuery,
  useGetV2AdminJotauditDashboardProvidersQuery: useGetAdminGlobalProvidersQuery,
  useGetV2AdminJotauditDashboardStatsQuery: useGetAdminGlobalStatsQuery,
  usePostV2AdminJotauditRunMutation: useRunAuditMutation,
  // Reaudit mutation
  usePostV2ProviderJotauditNotesByNoteIdReauditMutation: useReauditNoteMutation,
  // Status polling (lazy for manual control)
  useLazyGetV2ProviderJotauditStatusByThreadIdQuery: useLazyGetAuditStatusQuery,
} = jotauditApi;
